import { Component, OnInit, ViewEncapsulation, HostListener } from '@angular/core';
import { FormBuilder, Validators, FormGroup, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { EChartsOption } from 'echarts';

import { ReferenceDataService } from 'src/app/core/services/referencedata.service';
import { MessageService } from 'src/app/core/services/message.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { UtilisateurService } from 'src/app/core/services/utilisateur.service';
import { ZoneService } from 'src/app/core/services/zone.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { PersonnelRhService, PersonnelRHDTO } from 'src/app/core/services/personnel-rh.service';
import { Role } from 'src/app/core/enumerateurs/role';

@Component({
  selector: 'app-utilisateur',
  templateUrl: './utilisateur.component.html',
  styleUrls: ['./utilisateur.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UtilisateurComponent implements OnInit {

  formulaireUtilisateur!: FormGroup;
  idUtilisateur: number | null = null;
  estModeEdition = false;
  textBody = 'Créer un nouvel utilisateur';
  typesUtilisateurs: any[] = [];
  optionsRole: { label: string, value: string }[] = [];
  zonesActives: any[] = [];
  zonesFiltrees: any[] = [];
  livreursDisponibles: any[] = [];
  rechercheZone = '';
  dropdownZoneOuvert = false;
  chargement = false;
  uploadContratEnCours = false;
  fichiersContratSelectionnes: File[] = [];
  masquerMotDePasse = true;
  masquerConfirmationMotDePasse = true;
  returnUrl = '/utilisateur/lister';

  /** Données livreur (uniquement si role = LIVREUR) — palier, courses, gains, note. */
  statsLivreur: any = null;
  /** Profil complet livreur — véhicule actuel + documents. */
  profilLivreur: any = null;
  chargementProfilLivreur = false;

  /** Activer la section Informations RH (visible si role livreur, manager, etc.) */
  activerInfoRH = false;
  fichePersonnelExistante: PersonnelRHDTO | null = null;
  enregistrementRH = false;

  /** Historique primes mensuelles du livreur (12 derniers mois max). */
  historiquePrimes: any[] = [];
  chargementHistoriquePrimes = false;
  /** Relevé du mois en cours (somme livraisons, points, prime estimée). */
  releveMoisCourant: any = null;
  generationPrimeEnCours = false;

  typesContratRH = ['CDI', 'CDD', 'PRESTATAIRE', 'SOUS_TRAITANT', 'STAGIAIRE'];
  modesRemunRH = ['MENSUEL', 'PAR_LIVRAISON', 'MIXTE'];
  libellesRH: Record<string, string> = {
    CDI: 'CDI', CDD: 'CDD', PRESTATAIRE: 'Prestataire', SOUS_TRAITANT: 'Sous-traitant', STAGIAIRE: 'Stagiaire',
    MENSUEL: 'Mensuel fixe', PAR_LIVRAISON: 'Par livraison', MIXTE: 'Mixte (base + points)'
  };

  constructor(
    private fb: FormBuilder,
    public utilisateurService: UtilisateurService,
    private referenceDataService: ReferenceDataService,
    private zoneService: ZoneService,
    private messageService: MessageService,
    private toastService: ToastService,
    private authService: AuthService,
    private personnelRhService: PersonnelRhService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.formulaireUtilisateur = this.fb.group({
      id: [null],
      username: [''],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      nom: ['', [Validators.required, Validators.minLength(2)]],
      prenom: ['', [Validators.required, Validators.minLength(2)]],
      telephone: ['', [Validators.pattern(/^[0-9]{10}$/)]],
      adresse: [''],
      role: ['', [Validators.required]],
      typeUtilisateur: ['', [Validators.required]],
      zoneIds: [[]],
      livreurIds: [[]],
      contratBaseTarification: [''],
      contratTarificationParticuliere: [''],
      contratPiecesJustificatives: [''],
      // — Informations RH (optionnelles, créent une PersonnelRH à la sauvegarde) —
      rhNumeroMatricule: [''],
      rhPoste: [''],
      rhTypeContrat: ['CDI'],
      rhModeRemuneration: ['MENSUEL'],
      rhSalaireBase: [null],
      rhTarifParLivraison: [null],
      rhDateEmbauche: [''],
      rhDateFin: [''],
      rhNotes: ['']
    }, { validators: this.validateurCorrespondanceMotDePasse });
  }

  get rhModeCourant(): string { return this.formulaireUtilisateur.get('rhModeRemuneration')?.value || ''; }

  naviguerRetour(): void {
    this.router.navigateByUrl(this.returnUrl);
  }

  basculerInfoRH(): void { this.activerInfoRH = !this.activerInfoRH; }

  validateurCorrespondanceMotDePasse(controle: AbstractControl): ValidationErrors | null {
    const password = controle.get('password');
    const confirmPassword = controle.get('confirmPassword');
    if (!password || !confirmPassword) {
      return null;
    }
    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  private prepareUtilisateurPourMiseAJour(donnees: any): any {
    const estResponsableZone = this.estResponsableZoneSelectionne(donnees.typeUtilisateur);
    const livreurIds = Array.isArray(donnees.livreurIds) ? donnees.livreurIds.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id)) : [];
    const zonesIds = (estResponsableZone && Array.isArray(donnees.zoneIds)) ? donnees.zoneIds.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id)) : [];
    const utilisateurNetwork = {
      prenom: donnees.prenom,
      nom: donnees.nom,
      email: donnees.email,
      contact: donnees.telephone || donnees.contact,
      adresse: donnees.adresse || '',
      typeUtilisateurId: donnees.typeUtilisateur,
      enabled: donnees.enabled !== undefined ? donnees.enabled : true,
      geolocalisationActive: donnees.geolocalisationActive !== undefined ? donnees.geolocalisationActive : false,
      longitude: donnees.longitude || null,
      latitude: donnees.latitude || null,
      zoneId: (estResponsableZone && zonesIds.length > 0) ? zonesIds[0] : null,
      zoneIds: zonesIds,
      livreurIds: estResponsableZone ? livreurIds : [],
      contratBaseTarification: this.estClientSousContratSelectionne(donnees.typeUtilisateur) ? (donnees.contratBaseTarification || null) : null,
      contratTarificationParticuliere: this.estClientSousContratSelectionne(donnees.typeUtilisateur) ? (donnees.contratTarificationParticuliere || null) : null,
      contratPiecesJustificatives: this.estClientSousContratSelectionne(donnees.typeUtilisateur) ? (donnees.contratPiecesJustificatives || null) : null
    };
    return utilisateurNetwork;
  }

  ngOnInit(): void {
    this.optionsRole = this.referenceDataService.enumVersOptions(Role);
    this.chargerLivreursDisponibles();
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/utilisateur/lister';

    this.formulaireUtilisateur.get('typeUtilisateur')?.valueChanges.subscribe((typeUtilisateurId) => {
      const roleDérivé = this.deriverRoleDepuisType(typeUtilisateurId);
      if (roleDérivé) {
        this.formulaireUtilisateur.patchValue({ role: roleDérivé }, { emitEvent: false });
      }
      if (!this.estResponsableZoneSelectionne(typeUtilisateurId) && !this.estLivreurAdminSelectionne()) {
        this.formulaireUtilisateur.patchValue({ zoneIds: [], livreurIds: [] }, { emitEvent: false });
      }
      if (!this.estClientSousContratSelectionne(typeUtilisateurId)) {
        this.formulaireUtilisateur.patchValue({
          contratBaseTarification: '',
          contratTarificationParticuliere: '',
          contratPiecesJustificatives: ''
        }, { emitEvent: false });
      }
    });

    this.route.params.subscribe(params => {
      if (params['id']) {
        this.idUtilisateur = +params['id'];
        this.estModeEdition = false;
        this.textBody = 'Afficher un utilisateur';
        this.formulaireUtilisateur.get('password')?.clearValidators();
        this.formulaireUtilisateur.get('password')?.setValidators([Validators.minLength(6)]);
        this.formulaireUtilisateur.get('confirmPassword')?.clearValidators();
        this.formulaireUtilisateur.get('confirmPassword')?.updateValueAndValidity();
        this.formulaireUtilisateur.get('password')?.updateValueAndValidity();

        // Charger types et zones en parallèle AVANT de charger l'utilisateur
        // pour éviter la race condition dans le listener valueChanges
        forkJoin({
          types: this.utilisateurService.obtenirTypesUtilisateurs(),
          zones: this.zoneService.obtenirToutesLesZones()
        }).subscribe({
          next: ({ types, zones }: any) => {
            const typesExistants = (Array.isArray(types) ? types : []) || [];
            const tousTypes = this.ajouterTypeResponsableZoneSiAbsent(typesExistants);
            this.typesUtilisateurs = this.filtrerTypesSelonRole(tousTypes);
            this.zonesActives = Array.isArray(zones) ? zones : [];
            this.zonesFiltrees = [...this.zonesActives];
            this.chargerUtilisateur(this.idUtilisateur!);
          },
          error: () => {
            this.toastService.erreur('Impossible de charger les données de référence');
          }
        });
      } else {
        // Mode création : charger types et zones indépendamment
        this.utilisateurService.obtenirTypesUtilisateurs().subscribe(
          (donnees: any) => {
            const typesExistants = (Array.isArray(donnees) ? donnees : []) || [];
            const tousTypes = this.ajouterTypeResponsableZoneSiAbsent(typesExistants);
            this.typesUtilisateurs = this.filtrerTypesSelonRole(tousTypes);
          },
          () => {
            this.toastService.erreur('Impossible de charger les types d\'utilisateurs');
          }
        );
        this.chargerZonesActives();
      }
    });
  }

  chargerUtilisateur(id: number): void {
    this.chargement = true;
    this.utilisateurService.obtenirUtilisateurParId(id).subscribe(
      (utilisateur) => {
        this.chargement = false;
        const roleValue = utilisateur.role && typeof utilisateur.role === 'object' ? (utilisateur.role as any).name : utilisateur.role;
        this.formulaireUtilisateur.patchValue({
          id: utilisateur.id,
          username: utilisateur.username,
          email: utilisateur.email,
          nom: utilisateur.nom,
          prenom: utilisateur.prenom,
          telephone: utilisateur.telephone ?? utilisateur.contact,
          adresse: utilisateur.adresse ?? '',
          role: roleValue ?? '',
          typeUtilisateur: this.resoudreTypeUtilisateurId(utilisateur),
          zoneIds: Array.isArray(utilisateur.zoneIds) && utilisateur.zoneIds.length > 0
            ? utilisateur.zoneIds
            : (utilisateur.zoneResponsableId ? [utilisateur.zoneResponsableId] : []),
          livreurIds: this.extraireLivreurIds(utilisateur),
          contratBaseTarification: utilisateur.contratBaseTarification ?? '',
          contratTarificationParticuliere: utilisateur.contratTarificationParticuliere ?? '',
          contratPiecesJustificatives: utilisateur.contratPiecesJustificatives ?? ''
        });

        // Charger profil complet livreur (véhicule + docs + stats) si pertinent
        const roleNom = (roleValue || '').toString().toUpperCase();
        if (roleNom === 'LIVREUR') {
          this.chargerProfilLivreur(id);
        } else {
          this.statsLivreur = null;
          this.profilLivreur = null;
        }

        // Charger la fiche RH éventuelle de cet utilisateur
        this.personnelRhService.getPersonnelByUserId(id).subscribe({
          next: (fiche) => {
            this.fichePersonnelExistante = fiche || null;
            if (fiche) {
              this.activerInfoRH = true;
              this.formulaireUtilisateur.patchValue({
                rhNumeroMatricule: fiche.numeroMatricule || '',
                rhPoste: fiche.poste || '',
                rhTypeContrat: fiche.typeContrat || 'CDI',
                rhModeRemuneration: fiche.modeRemuneration || 'MENSUEL',
                rhSalaireBase: fiche.salaireBase ?? null,
                rhTarifParLivraison: fiche.tarifParLivraison ?? null,
                rhDateEmbauche: fiche.dateEmbauche || '',
                rhDateFin: fiche.dateFin || '',
                rhNotes: fiche.notes || ''
              });
            }
          },
          error: () => { this.fichePersonnelExistante = null; }
        });
      },
      () => {
        this.chargement = false;
        this.toastService.erreur('Impossible de charger l\'utilisateur');
      }
    );
  }

  /** Charge les stats agrégées + profil complet (véhicule + documents) du livreur. */
  chargerProfilLivreur(id: number): void {
    this.chargementProfilLivreur = true;
    forkJoin({
      stats: this.utilisateurService.obtenirStatsLivreur(id),
      profil: this.utilisateurService.obtenirProfilLivreurComplet(id),
    }).subscribe({
      next: (res: any) => {
        this.statsLivreur = res.stats || null;
        this.profilLivreur = res.profil || null;
        this.chargementProfilLivreur = false;
      },
      error: () => {
        this.chargementProfilLivreur = false;
        this.statsLivreur = null;
        this.profilLivreur = null;
      }
    });
    // Charge en parallèle l'historique des primes + relevé du mois courant si une fiche RH existe
    this.chargerDonneesRHLivreur(id);
  }

  /** Historique primes mensuelles + relevé du mois en cours pour le livreur. */
  chargerDonneesRHLivreur(userId: number): void {
    this.chargementHistoriquePrimes = true;
    this.personnelRhService.getHistoriquePrimesParUser(userId).subscribe({
      next: (primes) => {
        this.historiquePrimes = primes || [];
        this.chargementHistoriquePrimes = false;
      },
      error: () => {
        this.historiquePrimes = [];
        this.chargementHistoriquePrimes = false;
      }
    });

    this.personnelRhService.getPersonnelByUserId(userId).subscribe({
      next: (fiche) => {
        if (!fiche?.id) return;
        const mois = this.moisCourant();
        this.personnelRhService.getReleve(fiche.id, mois).subscribe({
          next: (releve) => { this.releveMoisCourant = releve; },
          error: () => { this.releveMoisCourant = null; }
        });
      },
      error: () => { /* pas de fiche RH — silencieux */ }
    });
  }

  /** Format YYYY-MM du mois courant. */
  moisCourant(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  /** Libellé court du mois (ex: "2026-03" → "Mars 2026"). */
  formatMoisAnnee(moisAnnee: string): string {
    if (!moisAnnee || moisAnnee.length < 7) return moisAnnee || '';
    const [a, m] = moisAnnee.split('-');
    const noms = ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin',
                  'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];
    return `${noms[Number(m) - 1]} ${a}`;
  }

  /** Génère la prime du mois en cours pour ce livreur. */
  genererPrimeMoisCourant(): void {
    if (!this.fichePersonnelExistante?.id) return;
    this.generationPrimeEnCours = true;
    this.personnelRhService
      .genererPrime(this.fichePersonnelExistante.id, this.moisCourant())
      .subscribe({
        next: () => {
          this.generationPrimeEnCours = false;
          this.toastService.succes('Prime du mois générée avec succès');
          if (this.idUtilisateur) this.chargerDonneesRHLivreur(this.idUtilisateur);
        },
        error: (err) => {
          this.generationPrimeEnCours = false;
          this.toastService.erreur(err?.error?.message || 'Impossible de générer la prime (peut-être déjà existante).');
        }
      });
  }

  /** Données graphique 6 derniers mois pour echarts. */
  get optionsGraphiquePrimes(): EChartsOption {
    const primes = [...this.historiquePrimes]
      .sort((a, b) => a.moisAnnee.localeCompare(b.moisAnnee))
      .slice(-6);
    const labels = primes.map(p => this.formatMoisAnnee(p.moisAnnee));
    const livraisons = primes.map(p => p.nbLivraisons || 0);
    const primesValeur = primes.map(p => p.montantPrime || 0);
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['Livraisons', 'Prime (FCFA)'], top: 0 },
      grid: { left: 40, right: 40, top: 30, bottom: 30 },
      xAxis: { type: 'category', data: labels },
      yAxis: [
        { type: 'value', name: 'Livr.' },
        { type: 'value', name: 'FCFA', splitLine: { show: false } }
      ],
      series: [
        { name: 'Livraisons', type: 'bar', data: livraisons, itemStyle: { color: '#3b82f6' } },
        { name: 'Prime (FCFA)', type: 'line', yAxisIndex: 1, data: primesValeur, smooth: true, itemStyle: { color: '#10b981' } }
      ]
    };
  }

  /** Badge couleur du statut prime. */
  badgeStatutPrime(statut: string): string {
    const m: Record<string, string> = {
      EN_ATTENTE: 'badge-livreur',
      VALIDEE: 'badge-admin',
      PAYEE: 'badge-user',
      REJETEE: 'badge-danger'
    };
    return m[statut] || 'badge-default';
  }

  libelleStatutPrime(statut: string): string {
    const m: Record<string, string> = {
      EN_ATTENTE: 'En attente',
      VALIDEE: 'Validée',
      PAYEE: 'Payée',
      REJETEE: 'Rejetée'
    };
    return m[statut] || statut;
  }

  /** Format compact des gains : 1.2K, 145M, 3.5B */
  formatGainsCompact(montant: number | null | undefined): string {
    const v = Number(montant ?? 0);
    if (v === 0) return '0';
    if (v >= 1_000_000_000) return (v / 1_000_000_000).toFixed(1).replace('.0', '') + 'B';
    if (v >= 1_000_000)     return (v / 1_000_000).toFixed(1).replace('.0', '') + 'M';
    if (v >= 1_000)         return (v / 1_000).toFixed(1).replace('.0', '') + 'K';
    return String(v);
  }

  /** Libellé du palier suivant. */
  palierSuivant(niveau: number): string {
    switch (niveau) {
      case 1: return 'ARGENT';
      case 2: return 'OR';
      case 3: return 'PLATINE';
      default: return 'PLATINE';
    }
  }

  /** Documents groupés : perso (vehiculeId null) vs véhicule (vehiculeId défini). */
  documentsPerso(): any[] {
    const docs = this.profilLivreur?.documents || [];
    return docs.filter((d: any) => !d.vehiculeId);
  }
  documentsVehicule(): any[] {
    const docs = this.profilLivreur?.documents || [];
    return docs.filter((d: any) => !!d.vehiculeId);
  }

  // ── Véhicule ─────────────────────────────────────────────────────────────

  afficherFormulaireVehicule = false;
  enregistrementVehiculeEnCours = false;
  vehiculeForm: any = {
    typeFlotte: 'DEMATRAL',
    immatriculation: '', marque: '', modele: '', couleur: '', numeroChassis: '',
    nomProprietaire: '', contactProprietaire: '', typeContrat: '',
    montantParLivraison: null, montantMensuel: null,
    enService: true, disponible: true
  };

  ouvrirFormulaireVehicule(): void {
    const v = this.profilLivreur?.vehiculeActuel;
    if (v) {
      this.vehiculeForm = {
        typeFlotte: v.typeFlotte || 'DEMATRAL',
        immatriculation: v.immatriculation || '',
        marque: v.marque || '', modele: v.modele || '',
        couleur: v.couleur || '', numeroChassis: v.numeroChassis || '',
        nomProprietaire: v.nomProprietaire || '',
        contactProprietaire: v.contactProprietaire || '',
        typeContrat: v.typeContrat || '',
        montantParLivraison: v.montantParLivraison ?? null,
        montantMensuel: v.montantMensuel ?? null,
        enService: v.enService !== false, disponible: v.disponible !== false
      };
    } else {
      this.vehiculeForm = {
        typeFlotte: 'DEMATRAL', immatriculation: '', marque: '', modele: '',
        couleur: '', numeroChassis: '', nomProprietaire: '',
        contactProprietaire: '', typeContrat: '',
        montantParLivraison: null, montantMensuel: null,
        enService: true, disponible: true
      };
    }
    this.afficherFormulaireVehicule = true;
  }

  fermerFormulaireVehicule(): void {
    this.afficherFormulaireVehicule = false;
  }

  soumettreFormulaireVehicule(): void {
    const livreurId = this.formulaireUtilisateur.get('id')?.value;
    if (!livreurId) return;
    if (!this.vehiculeForm.immatriculation || !this.vehiculeForm.marque || !this.vehiculeForm.modele) {
      this.toastService.erreur('Immatriculation, marque et modèle sont obligatoires');
      return;
    }
    this.enregistrementVehiculeEnCours = true;
    const vehiculeExistantId = this.profilLivreur?.vehiculeActuel?.id;

    const op = vehiculeExistantId
      ? this.utilisateurService.modifierVehicule(vehiculeExistantId, this.vehiculeForm)
      : this.utilisateurService.creerVehicule(this.vehiculeForm);

    op.subscribe({
      next: (vehicule: any) => {
        const vId = vehicule?.id ?? vehiculeExistantId;
        if (!vehiculeExistantId && vId) {
          this.utilisateurService.assignerVehiculeAuLivreur(livreurId, vId).subscribe({
            next: () => {
              this.enregistrementVehiculeEnCours = false;
              this.afficherFormulaireVehicule = false;
              this.toastService.succes('Véhicule assigné avec succès');
              this.chargerProfilLivreur(livreurId);
            },
            error: () => {
              this.enregistrementVehiculeEnCours = false;
              this.toastService.erreur('Véhicule créé mais erreur lors de l\'assignation');
            }
          });
        } else {
          this.enregistrementVehiculeEnCours = false;
          this.afficherFormulaireVehicule = false;
          this.toastService.succes('Véhicule mis à jour avec succès');
          this.chargerProfilLivreur(livreurId);
        }
      },
      error: () => {
        this.enregistrementVehiculeEnCours = false;
        this.toastService.erreur('Erreur lors de l\'enregistrement du véhicule');
      }
    });
  }

  confirmerDesassignation(): void {
    const livreurId = this.formulaireUtilisateur.get('id')?.value;
    if (!livreurId) return;
    if (!confirm('Désassigner le véhicule de ce livreur ?')) return;
    this.utilisateurService.desassignerVehicule(livreurId).subscribe({
      next: () => {
        this.toastService.succes('Véhicule désassigné');
        this.chargerProfilLivreur(livreurId);
      },
      error: () => this.toastService.erreur('Erreur lors de la désassignation')
    });
  }

  // ── Documents ─────────────────────────────────────────────────────────────

  afficherFormulaireDocument = false;
  enregistrementDocumentEnCours = false;
  documentForm: any = {
    type: '', numero: '', dateEmission: '', dateExpiration: '', commentaire: ''
  };

  typeDocumentLibelle(type: string): string {
    const map: Record<string, string> = {
      CNI: 'CNI', PERMIS: 'Permis de conduire',
      CARTE_GRISE: 'Carte grise', ATTESTATION_ASSURANCE: 'Attestation assurance'
    };
    return map[type] || type;
  }

  ouvrirFormulaireDocument(): void {
    this.documentForm = { type: 'CNI', numero: '', dateEmission: '', dateExpiration: '', commentaire: '' };
    this.afficherFormulaireDocument = true;
  }

  fermerFormulaireDocument(): void {
    this.afficherFormulaireDocument = false;
  }

  soumettreFormulaireDocument(): void {
    const livreurId = this.formulaireUtilisateur.get('id')?.value;
    if (!livreurId) return;
    if (!this.documentForm.type) {
      this.toastService.erreur('Veuillez sélectionner un type de document');
      return;
    }
    const typesVehicule = ['CARTE_GRISE', 'ATTESTATION_ASSURANCE'];
    if (typesVehicule.includes(this.documentForm.type) && !this.profilLivreur?.vehiculeActuel) {
      this.toastService.erreur('Assignez d\'abord un véhicule avant d\'ajouter ce type de document');
      return;
    }
    this.enregistrementDocumentEnCours = true;
    const payload: any = {
      livreurId,
      type: this.documentForm.type,
      numero: this.documentForm.numero || null,
      dateEmission: this.documentForm.dateEmission || null,
      dateExpiration: this.documentForm.dateExpiration || null,
      commentaire: this.documentForm.commentaire || null
    };
    this.utilisateurService.creerDocumentLivreur(payload).subscribe({
      next: () => {
        this.enregistrementDocumentEnCours = false;
        this.afficherFormulaireDocument = false;
        this.toastService.succes('Document ajouté avec succès');
        this.chargerProfilLivreur(livreurId);
      },
      error: () => {
        this.enregistrementDocumentEnCours = false;
        this.toastService.erreur('Erreur lors de l\'ajout du document');
      }
    });
  }

  modifierStatutDoc(doc: any, statut: string): void {
    const livreurId = this.formulaireUtilisateur.get('id')?.value;
    this.utilisateurService.modifierStatutDocument(doc.id, statut).subscribe({
      next: () => {
        this.toastService.succes(`Document marqué : ${statut}`);
        if (livreurId) this.chargerProfilLivreur(livreurId);
      },
      error: () => this.toastService.erreur('Erreur lors de la mise à jour du statut')
    });
  }

  supprimerDoc(doc: any): void {
    const livreurId = this.formulaireUtilisateur.get('id')?.value;
    if (!confirm('Supprimer ce document ?')) return;
    this.utilisateurService.supprimerDocumentLivreur(doc.id).subscribe({
      next: () => {
        this.toastService.succes('Document supprimé');
        if (livreurId) this.chargerProfilLivreur(livreurId);
      },
      error: () => this.toastService.erreur('Erreur lors de la suppression')
    });
  }

  private resoudreTypeUtilisateurId(utilisateur: any): any {
    const typeId = utilisateur.typeUtilisateurId ?? utilisateur.typeUtilisateur ?? '';
    // Si l'ID retourné par l'API correspond à un type dans la liste filtrée, l'utiliser
    if (typeId && this.typesUtilisateurs.find(t => String(t.id) === String(typeId))) {
      return typeId;
    }
    // Sinon, le type a probablement été filtré (ex: GESTIONNAIRE_DE_ZONE → RESPONSABLE DE ZONE)
    // Faire correspondre via le rôle de l'utilisateur
    const role = utilisateur.role && typeof utilisateur.role === 'object'
      ? (utilisateur.role as any).name
      : utilisateur.role;
    const roleStr = (role || '').toString().toUpperCase();
    const libellesCibles: Record<string, string> = {
      'RESPONSABLE_ZONE': 'RESPONSABLE DE ZONE',
      'COMPTABLE_ZONE': 'COMPTABLE DE ZONE',
      'COMPTABLE_GENERAL': 'COMPTABLE GENERAL',
      'MANAGER': 'MANAGER',
      'ADMIN': 'ADMIN',
      'LIVREUR': 'LIVREUR',
    };
    const libelleCible = libellesCibles[roleStr];
    if (libelleCible) {
      const typeMatche = this.typesUtilisateurs.find(t =>
        (t.libelle || '').toString().trim().toUpperCase() === libelleCible
      );
      if (typeMatche) return typeMatche.id;
    }
    return typeId;
  }

  onSubmit() {
    if (this.formulaireUtilisateur.invalid) {
      this.formulaireUtilisateur.markAllAsTouched();
      if (this.formulaireUtilisateur.errors?.['passwordMismatch']) {
        this.toastService.erreur('Les mots de passe ne correspondent pas');
      } else {
        this.toastService.erreur('Veuillez remplir tous les champs obligatoires correctement');
      }
      return;
    }

    const typeSelectionne = this.formulaireUtilisateur.get('typeUtilisateur')?.value;
    if (this.estResponsableZoneSelectionne(typeSelectionne)) {
      const zoneIds = this.formulaireUtilisateur.get('zoneIds')?.value;
      if (!Array.isArray(zoneIds) || zoneIds.length === 0) {
        this.toastService.erreur('Veuillez assigner au moins une zone au responsable de zone');
        return;
      }
    }

    this.chargement = true;
    const donneesFormulaire = { ...this.formulaireUtilisateur.value };
    const motDePasseAChanger = donneesFormulaire.password;
    const idUtilisateur = donneesFormulaire.id;

    if (this.estModeEdition) {
      // Mode édition : préparer les données nettoyées pour le backend
      const donneesMaj = this.prepareUtilisateurPourMiseAJour(donneesFormulaire);

      // Ajouter le username s'il a changé
      if (donneesFormulaire.username && donneesFormulaire.username.trim()) {
        donneesMaj.username = donneesFormulaire.username.trim();
      }

      this.utilisateurService.modifierUtilisateur({ ...donneesMaj, id: idUtilisateur }).subscribe(
        () => {
          const finaliser = () => {
            if (this.activerInfoRH && idUtilisateur) {
              this.sauvegarderInfoRH(idUtilisateur, donneesFormulaire, () => {
                this.chargement = false;
                this.toastService.succes('Utilisateur et fiche RH mis à jour');
                this.naviguerRetour();
              });
            } else {
              this.chargement = false;
              this.toastService.succes('Utilisateur modifié avec succès');
              this.naviguerRetour();
            }
          };

          if (motDePasseAChanger && motDePasseAChanger.trim()) {
            this.utilisateurService.changerMotDePasse(idUtilisateur, motDePasseAChanger).subscribe(
              () => { finaliser(); },
              (erreur) => {
                this.chargement = false;
                this.toastService.erreur('Utilisateur modifié mais erreur lors du changement du mot de passe: ' + this.messageService.afficherMessageErreur(erreur));
              }
            );
          } else {
            finaliser();
          }
        },
        (erreur) => {
          this.chargement = false;
          this.toastService.erreur(this.messageService.afficherMessageErreur(erreur));
        }
      );
    } else {
      // Mode création : préparer les données pour l'API
      const donneesCreation = {
        username: donneesFormulaire.username || donneesFormulaire.email,
        password: donneesFormulaire.password,
        email: donneesFormulaire.email,
        nom: donneesFormulaire.nom,
        prenom: donneesFormulaire.prenom,
        contact: donneesFormulaire.telephone || donneesFormulaire.contact || '',
        adresse: donneesFormulaire.adresse || '',
        role: donneesFormulaire.role,
        typeUtilisateur: donneesFormulaire.typeUtilisateur,
        zoneIds: (this.estResponsableZoneSelectionne(donneesFormulaire.typeUtilisateur) || this.estLivreurAdminSelectionne())
          ? (Array.isArray(donneesFormulaire.zoneIds) ? donneesFormulaire.zoneIds.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id)) : [])
          : [],
        zoneId: (this.estResponsableZoneSelectionne(donneesFormulaire.typeUtilisateur) || this.estLivreurAdminSelectionne())
          ? (Array.isArray(donneesFormulaire.zoneIds) && donneesFormulaire.zoneIds.length > 0 ? Number(donneesFormulaire.zoneIds[0]) : null)
          : null,
        livreurIds: this.estResponsableZoneSelectionne(donneesFormulaire.typeUtilisateur)
          ? (Array.isArray(donneesFormulaire.livreurIds) ? donneesFormulaire.livreurIds.map((id: any) => Number(id)) : [])
          : [],
        contratBaseTarification: this.estClientSousContratSelectionne(donneesFormulaire.typeUtilisateur) ? (donneesFormulaire.contratBaseTarification || null) : null,
        contratTarificationParticuliere: this.estClientSousContratSelectionne(donneesFormulaire.typeUtilisateur) ? (donneesFormulaire.contratTarificationParticuliere || null) : null,
        contratPiecesJustificatives: this.estClientSousContratSelectionne(donneesFormulaire.typeUtilisateur) ? (donneesFormulaire.contratPiecesJustificatives || null) : null
      };

      this.utilisateurService.enregistrerUtilisateur(donneesCreation).subscribe(
        (utilisateurCree: any) => {
          const nouveauId = utilisateurCree?.id || utilisateurCree?.userId;
          if (this.activerInfoRH && nouveauId) {
            this.sauvegarderInfoRH(nouveauId, donneesFormulaire, () => {
              this.chargement = false;
              this.toastService.succes('Utilisateur et fiche RH créés avec succès');
              this.naviguerRetour();
            });
          } else {
            this.chargement = false;
            this.toastService.succes('Utilisateur créé avec succès');
            this.naviguerRetour();
          }
        },
        (erreur) => {
          this.chargement = false;
          this.toastService.erreur(this.messageService.afficherMessageErreur(erreur));
        }
      );
    }
  }

  /** Crée ou met à jour la fiche RH pour l'utilisateur donné. */
  private sauvegarderInfoRH(userId: number, donnees: any, callback: () => void): void {
    const dtoRH: PersonnelRHDTO = {
      userId,
      typeContrat: donnees.rhTypeContrat || 'CDI',
      modeRemuneration: donnees.rhModeRemuneration || 'MENSUEL',
      salaireBase: donnees.rhSalaireBase || undefined,
      tarifParLivraison: donnees.rhTarifParLivraison || undefined,
      dateEmbauche: donnees.rhDateEmbauche || new Date().toISOString().substring(0, 10),
      dateFin: donnees.rhDateFin || undefined,
      poste: donnees.rhPoste || undefined,
      numeroMatricule: donnees.rhNumeroMatricule || undefined,
      notes: donnees.rhNotes || undefined
    };

    const op = this.fichePersonnelExistante?.id
      ? this.personnelRhService.modifierPersonnel(this.fichePersonnelExistante.id, dtoRH)
      : this.personnelRhService.creerPersonnel(dtoRH);

    op.subscribe({
      next: () => callback(),
      error: () => {
        this.toastService.erreur('Utilisateur créé mais erreur lors de la création de la fiche RH');
        callback();
      }
    });
  }

  obtenirMessageErreur(nomChamp: string): string {
    const controle = this.formulaireUtilisateur.get(nomChamp);
    if (!controle || !controle.errors || !controle.touched) {
      return '';
    }
    if (controle.errors['required']) {
      return 'Ce champ est obligatoire';
    }
    if (controle.errors['email']) {
      return 'Adresse e-mail invalide';
    }
    if (controle.errors['minlength']) {
      return `Minimum ${controle.errors['minlength'].requiredLength} caractères`;
    }
    if (controle.errors['pattern']) {
      return 'Format invalide (10 chiffres attendus)';
    }
    return '';
  }

  obtenirLibelleType(idType: any): string {
    if (!idType) return '';
    const type = this.typesUtilisateurs.find(t => t.id === idType);
    return type ? type.libelle : '';
  }

  /** Dérive automatiquement le rôle Spring Security depuis le libellé du type. */
  deriverRoleDepuisType(typeId: any): string {
    if (!typeId) return '';
    const type = this.typesUtilisateurs.find(t => String(t.id) === String(typeId));
    const libelle = (type?.libelle || '').toString().trim().toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (libelle.includes('ADMIN')) return 'ADMIN';
    if (libelle.includes('RESPONSABLE') && libelle.includes('ZONE')) return 'RESPONSABLE_ZONE';
    if (libelle.includes('COMPTABLE') && libelle.includes('ZONE')) return 'COMPTABLE_ZONE';
    if (libelle.includes('COMPTABLE') && (libelle.includes('GENERAL') || libelle.includes('GENERALE'))) return 'COMPTABLE_GENERAL';
    if (libelle.includes('DEMATRAL')) return 'MANAGER';
    if (libelle.includes('MANAGER')) return 'MANAGER';
    if (libelle === 'LIVREUR') return 'LIVREUR';
    return 'USER';
  }

  roleLibelle(role: string): string {
    const map: Record<string, string> = {
      ADMIN: 'Administrateur',
      MANAGER: 'Manager',
      LIVREUR: 'Livreur',
      RESPONSABLE_ZONE: 'Responsable de zone',
      COMPTABLE_ZONE: 'Comptable de zone',
      COMPTABLE_GENERAL: 'Comptable général',
      USER: 'Utilisateur standard',
    };
    return map[role] ?? role;
  }

  roleDescription(role: string): string {
    const map: Record<string, string> = {
      ADMIN: 'Accès complet à toutes les fonctionnalités',
      MANAGER: 'Gestion des commandes, livreurs et rapports',
      LIVREUR: 'Accès à ses courses et suivi de livraisons',
      RESPONSABLE_ZONE: 'Gestion des commandes interurbaines de sa zone',
      COMPTABLE_ZONE: 'Contrôle des encaissements et règlements de sa zone',
      COMPTABLE_GENERAL: 'Supervision comptable de toutes les zones',
      USER: 'Création de commandes uniquement',
    };
    return map[role] ?? '';
  }

  roleIcone(role: string): string {
    const map: Record<string, string> = {
      ADMIN: 'fal fa-shield-check',
      MANAGER: 'fal fa-user-tie',
      LIVREUR: 'fal fa-motorcycle',
      RESPONSABLE_ZONE: 'fal fa-map-marker-alt',
      COMPTABLE_ZONE: 'fal fa-calculator',
      COMPTABLE_GENERAL: 'fal fa-file-invoice-dollar',
      USER: 'fal fa-user',
    };
    return map[role] ?? 'fal fa-user';
  }

  estLivreurSelectionne(): boolean {
    const typeId = this.formulaireUtilisateur.get('typeUtilisateur')?.value;
    if (typeId) {
      const type = this.typesUtilisateurs.find(t => String(t.id) === String(typeId));
      const libelle = (type?.libelle || '').toString().trim().toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (libelle === 'LIVREUR') return true;
    }
    const role = (this.formulaireUtilisateur.get('role')?.value || '').toString().trim().toUpperCase();
    return role === 'LIVREUR';
  }

  estResponsableZoneSelectionne(typeUtilisateurId?: any): boolean {
    const typeId = typeUtilisateurId ?? this.formulaireUtilisateur.get('typeUtilisateur')?.value;
    if (!typeId) return false;
    const type = this.typesUtilisateurs.find(t => String(t.id) === String(typeId));
    const libelle = (type?.libelle || '').toString().trim().toUpperCase();
    return libelle === 'RESPONSABLE DE ZONE';
  }

  estLivreurAdminSelectionne(): boolean {
    const role = (this.authService.infosUtilisateur?.role || '').toString().toUpperCase();
    if (role !== 'ADMIN' && role !== 'MANAGER') return false;
    return this.estLivreurSelectionne();
  }

  doitAfficherZone(): boolean {
    return this.estResponsableZoneSelectionne() || this.estLivreurAdminSelectionne();
  }

  labelZone(): string {
    return this.estLivreurAdminSelectionne() ? 'Zone du livreur' : 'Zone responsable *';
  }

  estClientSousContratSelectionne(typeUtilisateurId?: any): boolean {
    const typeId = typeUtilisateurId ?? this.formulaireUtilisateur.get('typeUtilisateur')?.value;
    if (!typeId) {
      return false;
    }
    const type = this.typesUtilisateurs.find(t => String(t.id) === String(typeId));
    const libelle = (type?.libelle || '').toString().trim().toUpperCase();
    return libelle === 'CLIENT SOUS CONTRAT';
  }

  obtenirNomZone(zoneId: any): string {
    if (!zoneId) return '—';
    const zone = this.zonesActives.find((z) => String(z?.id) === String(zoneId));
    return zone?.nom || '—';
  }

  obtenirNomsZones(zoneIds: any[]): string {
    if (!Array.isArray(zoneIds) || zoneIds.length === 0) return '—';
    return zoneIds.map(id => this.obtenirNomZone(id)).filter(n => n !== '—').join(', ') || '—';
  }

  obtenirNomsLivreursSelectionnes(livreurIds: any[] | null | undefined): string {
    if (!Array.isArray(livreurIds) || livreurIds.length === 0) return '—';
    const libelles = livreurIds
      .map((id) => this.livreursDisponibles.find((l) => String(l?.id) === String(id)))
      .filter(Boolean)
      .map((livreur: any) => `${livreur?.prenom || ''} ${livreur?.nom || ''}`.trim())
      .filter(Boolean);
    return libelles.length > 0 ? libelles.join(', ') : '—';
  }

  private chargerZonesActives(): void {
    this.zoneService.obtenirToutesLesZones().subscribe({
      next: (zones) => {
        this.zonesActives = Array.isArray(zones) ? zones : [];
        this.zonesFiltrees = [...this.zonesActives];
      },
      error: () => {
        this.zonesActives = [];
        this.zonesFiltrees = [];
        this.toastService.erreur('Impossible de charger les zones actives');
      }
    });
  }

  filtrerZonesPourChosen(): void {
    const terme = (this.rechercheZone || '').trim().toLowerCase();
    if (!terme) {
      this.zonesFiltrees = [...this.zonesActives];
      return;
    }
    this.zonesFiltrees = this.zonesActives.filter((z: any) =>
      (z?.nom || '').toString().toLowerCase().includes(terme)
      || (z?.description || '').toString().toLowerCase().includes(terme)
      || (z?.villeNom || '').toString().toLowerCase().includes(terme)
    );
  }

  /** Zones regroupées par ville pour l'affichage dans le dropdown. */
  get zonesGroupeesParVille(): { villeId: any; villeNom: string; zones: any[] }[] {
    const groupes = new Map<string, { villeId: any; villeNom: string; zones: any[] }>();
    const sanVille = { villeId: null, villeNom: '(Sans ville)', zones: [] as any[] };

    for (const z of this.zonesFiltrees) {
      if (z?.villeId) {
        const key = String(z.villeId);
        if (!groupes.has(key)) {
          groupes.set(key, { villeId: z.villeId, villeNom: z.villeNom || key, zones: [] });
        }
        groupes.get(key)!.zones.push(z);
      } else {
        sanVille.zones.push(z);
      }
    }

    const result = Array.from(groupes.values()).sort((a, b) => a.villeNom.localeCompare(b.villeNom));
    if (sanVille.zones.length > 0) result.push(sanVille);
    return result;
  }

  toggleDropdownZone(event: Event): void {
    event.stopPropagation();
    this.dropdownZoneOuvert = !this.dropdownZoneOuvert;
    if (this.dropdownZoneOuvert) {
      this.rechercheZone = '';
      this.filtrerZonesPourChosen();
    }
  }

  selectionnerZone(zone: any): void {
    const ids: number[] = [...(this.formulaireUtilisateur.get('zoneIds')?.value || [])];
    const idx = ids.findIndex(id => String(id) === String(zone.id));
    if (idx >= 0) {
      ids.splice(idx, 1);
    } else {
      ids.push(Number(zone.id));
    }
    this.formulaireUtilisateur.patchValue({ zoneIds: ids });
  }

  selectionnerVille(groupe: { villeId: any; villeNom: string; zones: any[] }): void {
    const ids: number[] = [...(this.formulaireUtilisateur.get('zoneIds')?.value || [])];
    const zonesVilleIds = groupe.zones.map((z: any) => Number(z.id));
    const toutesSelectionnees = zonesVilleIds.every(id => ids.includes(id));

    if (toutesSelectionnees) {
      // Décocher toutes les zones de cette ville
      const nouvIds = ids.filter(id => !zonesVilleIds.includes(id));
      this.formulaireUtilisateur.patchValue({ zoneIds: nouvIds });
    } else {
      // Cocher toutes les zones de cette ville
      const nouvIds = [...ids];
      for (const id of zonesVilleIds) {
        if (!nouvIds.includes(id)) nouvIds.push(id);
      }
      this.formulaireUtilisateur.patchValue({ zoneIds: nouvIds });
    }
  }

  retirerZone(zoneId: any, event: Event): void {
    event.stopPropagation();
    const ids: number[] = (this.formulaireUtilisateur.get('zoneIds')?.value || []).filter((id: any) => String(id) !== String(zoneId));
    this.formulaireUtilisateur.patchValue({ zoneIds: ids });
  }

  effacerZone(event: Event): void {
    event.stopPropagation();
    this.formulaireUtilisateur.patchValue({ zoneIds: [] });
  }

  fermerDropdownZone(): void {
    this.dropdownZoneOuvert = false;
  }

  estZoneSelectionnee(zoneId: any): boolean {
    const ids: any[] = this.formulaireUtilisateur.get('zoneIds')?.value || [];
    return ids.some(id => String(id) === String(zoneId));
  }

  estVilleSelectionnee(groupe: { villeId: any; zones: any[] }): boolean {
    const ids: any[] = this.formulaireUtilisateur.get('zoneIds')?.value || [];
    return groupe.zones.length > 0 && groupe.zones.every((z: any) => ids.some(id => String(id) === String(z.id)));
  }

  estVillePartielSelectionnee(groupe: { villeId: any; zones: any[] }): boolean {
    const ids: any[] = this.formulaireUtilisateur.get('zoneIds')?.value || [];
    const nb = groupe.zones.filter((z: any) => ids.some(id => String(id) === String(z.id))).length;
    return nb > 0 && nb < groupe.zones.length;
  }

  zonesSelectionnees(): any[] {
    const ids: any[] = this.formulaireUtilisateur.get('zoneIds')?.value || [];
    return ids.map(id => this.zonesActives.find((z: any) => String(z?.id) === String(id))).filter(Boolean);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.dropdownZoneOuvert = false;
  }

  private chargerLivreursDisponibles(): void {
    this.utilisateurService.obtenirTousUtilisateurs().subscribe({
      next: (utilisateurs) => {
        const liste = Array.isArray(utilisateurs) ? utilisateurs : [];
        this.livreursDisponibles = liste.filter((u: any) => (u?.role || '').toString().toUpperCase() === 'LIVREUR');
      },
      error: () => {
        this.livreursDisponibles = [];
        this.toastService.erreur('Impossible de charger la liste des livreurs');
      }
    });
  }

  private filtrerTypesSelonRole(types: any[]): any[] {
    const roleCreateur = (this.authService.infosUtilisateur?.role || '').toString().toUpperCase();
    // Types toujours masqués
    const exclusGlobal = ['GESTIONNAIRE DE ZONE', 'DEMATRAL', 'USER'];
    const filtrés = types.filter((t: any) => {
      const lib = (t?.libelle || '').toString().trim().toUpperCase();
      return !exclusGlobal.includes(lib);
    });
    if (roleCreateur === 'RESPONSABLE_ZONE') {
      const autorisés = ['LIVREUR', 'CLIENT STANDART', 'CLIENT SOUS CONTRAT', 'COMPTABLE DE ZONE'];
      return filtrés.filter((t: any) => {
        const lib = (t?.libelle || '').toString().trim().toUpperCase();
        return autorisés.includes(lib);
      });
    }
    return filtrés;
  }

  private ajouterTypeResponsableZoneSiAbsent(types: any[]): any[] {
    const existeResponsable = types.some((type: any) => {
      const libelle = (type?.libelle || '').toString().trim().toUpperCase();
      return libelle === 'RESPONSABLE DE ZONE';
    });
    const existeComptableZone = types.some((type: any) =>
      (type?.libelle || '').toString().trim().toUpperCase() === 'COMPTABLE DE ZONE'
    );
    const existeComptableGeneral = types.some((type: any) =>
      (type?.libelle || '').toString().trim().toUpperCase() === 'COMPTABLE GENERAL'
    );

    const ajouts: any[] = [];
    if (!existeResponsable) ajouts.push({ id: 'RESPONSABLE_ZONE', libelle: 'RESPONSABLE DE ZONE' });
    if (!existeComptableZone) ajouts.push({ id: 'COMPTABLE_ZONE', libelle: 'COMPTABLE DE ZONE' });
    if (!existeComptableGeneral) ajouts.push({ id: 'COMPTABLE_GENERAL', libelle: 'COMPTABLE GENERAL' });
    return [...types, ...ajouts];
  }

  private extraireLivreurIds(utilisateur: any): number[] {
    const idsDepuisTableau = Array.isArray(utilisateur?.livreurIds)
      ? utilisateur.livreurIds
      : Array.isArray(utilisateur?.livreurs)
        ? utilisateur.livreurs.map((livreur: any) => livreur?.id)
        : [];
    return idsDepuisTableau
      .map((id: any) => Number(id))
      .filter((id: number) => Number.isFinite(id));
  }

  onFichiersContratSelectionnes(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input?.files ? Array.from(input.files) : [];
    this.fichiersContratSelectionnes = files;
  }

  uploaderPiecesContrat(): void {
    const userId = this.formulaireUtilisateur.get('id')?.value;
    if (!userId) {
      this.toastService.erreur('Créez d’abord le client, puis joignez les pièces en modification.');
      return;
    }
    if (!this.fichiersContratSelectionnes.length) {
      this.toastService.erreur('Aucun fichier sélectionné.');
      return;
    }
    this.uploadContratEnCours = true;
    const uploads = this.fichiersContratSelectionnes.map((f) => this.utilisateurService.uploadPieceContrat(Number(userId), f));
    forkJoin(uploads).subscribe({
      next: (responses: any[]) => {
        const last = responses[responses.length - 1];
        this.formulaireUtilisateur.patchValue({
          contratPiecesJustificatives: last?.contratPiecesJustificatives ?? this.formulaireUtilisateur.get('contratPiecesJustificatives')?.value
        });
        this.uploadContratEnCours = false;
        this.toastService.succes(`${responses.length} pièce(s) contrat uploadée(s)`);
        this.fichiersContratSelectionnes = [];
      },
      error: () => {
        this.uploadContratEnCours = false;
        this.toastService.erreur('Erreur lors de l’upload des pièces contrat');
      }
    });
  }

  piecesContratListe(): string[] {
    const brut = (this.formulaireUtilisateur.get('contratPiecesJustificatives')?.value || '').toString();
    return brut.split(',').map((x) => x.trim()).filter((x) => !!x);
  }

  pieceEstImage(url: string): boolean {
    const u = (url || '').toLowerCase();
    return ['.png', '.jpg', '.jpeg', '.webp', '.gif'].some((ext) => u.includes(ext));
  }

  pieceEstPdf(url: string): boolean {
    return (url || '').toLowerCase().includes('.pdf');
  }
}
